import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  FormControlLabel,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  IconButton,
  useTheme,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import CustomTypography from "../form/CustomTypography";
import { ProjectHandoverData } from "../../../../models/ProjectManagement/ProjectProductsSourcingVariants";
import { formatDate, formatDateWithoutTime } from "../functions/formatDate";
import { useStores } from "../../../../use-stores";
import { EmployeeRoles } from "../../../../models/Employees/EmployeeRoles";

interface HandoverCallDetailsPopupProps {
  open: boolean;
  data: ProjectHandoverData | null;
  onClose: () => void;
  onSave: (data: ProjectHandoverData) => void;
  sendEmail: (data: ProjectHandoverData, preview_email: boolean) => void;
}

const HandoverCallDetailsPopup: React.FC<HandoverCallDetailsPopupProps> = ({
  open,
  data,
  onClose,
  onSave,
  sendEmail,
}) => {
  const { userStore } = useStores();
  const theme = useTheme();
  const [formData, setFormData] = useState<ProjectHandoverData | null>(null);

  useEffect(() => {
    setFormData(
      data || {
        log_files_required: undefined,
        log_files_received: undefined,
        size_mix_files_required: undefined,
        size_mix_files_received: undefined,
        insert_content_required: undefined,
        insert_content_received: undefined,
        address_file_required: undefined,
        address_file_received: undefined,
        products: [],
      }
    );
  }, [data]);

  const handleRequirementChange = (
    key: keyof ProjectHandoverData,
    required: boolean
  ) => {
    setFormData((prev) => {
      if (!prev) return null;
      return {
        ...prev,
        [key]: required ? 1 : 0,
      };
    });
  };

  const handleReceivedChange = (
    key: keyof ProjectHandoverData,
    received: boolean
  ) => {
    setFormData((prev) => {
      if (!prev) return null;
      return {
        ...prev,
        [key]: received ? 1 : 0,
      };
    });
  };

  const RequirementSection = ({
    title,
    requiredKey,
    receivedKey,
    description,
  }: {
    title: string;
    requiredKey: keyof ProjectHandoverData;
    receivedKey: keyof ProjectHandoverData;
    description: string;
  }) => (
    <Box sx={{ mb: 3 }}>
      <CustomTypography
        variant="heading6"
        style={{ fontWeight: 600, marginBottom: 6 }}
      >
        {title}
      </CustomTypography>
      <CustomTypography
        variant="body2"
        style={{
          marginBottom: 20,
          lineHeight: "1.2",
          color: theme.palette.text.secondary,
        }}
      >
        {description}
      </CustomTypography>

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", lg: "row" },
          gap: { xs: 0, lg: 4 },
          alignItems: { lg: "flex-start" },
        }}
      >
        {/* First Question - Left Side */}
        <Box sx={{ flex: 1, mb: { xs: 2, lg: 0 } }}>
          <CustomTypography
            variant="body2"
            style={{ marginBottom: 4, fontWeight: 500, lineHeight: "1.2" }}
          >
            Is this required for this order?
          </CustomTypography>
          <RadioGroup
            row
            value={
              formData?.[requiredKey] === null ||
              formData?.[requiredKey] === undefined
                ? ""
                : String(formData?.[requiredKey] === 1)
            }
            onChange={(e) =>
              handleRequirementChange(requiredKey, e.target.value === "true")
            }
            sx={{
              "& .MuiFormControlLabel-label": {
                fontWeight: 500,
                fontSize: "0.9rem",
              },
              "& .MuiRadio-root": {
                padding: "4px",
                marginLeft: "4px",
                "& .MuiSvgIcon-root": {
                  fontSize: "1.1rem",
                },
              },
            }}
          >
            <FormControlLabel
              value={true}
              control={<Radio size="small" />}
              label="Yes"
            />
            <FormControlLabel
              value={false}
              control={<Radio size="small" />}
              label="No"
            />
          </RadioGroup>
        </Box>

        {/* Second Question - Right Side (only when required) */}
        {formData?.[requiredKey] === 1 && (
          <Box sx={{ flex: 1 }}>
            <CustomTypography
              variant="body2"
              style={{ marginBottom: 4, fontWeight: 500, lineHeight: "1.2" }}
            >
              Did the client send this over?
            </CustomTypography>
            <RadioGroup
              row
              value={
                formData?.[receivedKey] === null ||
                formData?.[receivedKey] === undefined
                  ? ""
                  : String(formData?.[receivedKey] === 1)
              }
              onChange={(e) =>
                handleReceivedChange(receivedKey, e.target.value === "true")
              }
              sx={{
                "& .MuiFormControlLabel-label": {
                  fontWeight: 500,
                  fontSize: "0.9rem",
                },
                "& .MuiRadio-root": {
                  padding: "4px",
                  marginLeft: "4px",
                  "& .MuiSvgIcon-root": {
                    fontSize: "1.1rem",
                  },
                },
              }}
            >
              <FormControlLabel
                value={true}
                control={<Radio size="small" />}
                label="Yes"
              />
              <FormControlLabel
                value={false}
                control={<Radio size="small" />}
                label="No"
              />
            </RadioGroup>
          </Box>
        )}
      </Box>
    </Box>
  );

  // Don't render if formData is not initialized
  if (!formData) {
    return null;
  }

  // Check if all required questions are answered
  const isFormValid = () => {
    const requirements = [
      "log_files_required",
      "size_mix_files_required",
      "insert_content_required",
      "address_file_required",
    ];

    for (const req of requirements) {
      const required = formData[req as keyof ProjectHandoverData];

      // If required is null/undefined, form is not valid
      if (required === null || required === undefined) {
        return false;
      }

      // If required is 1 (true), check if received is also answered
      if (required === 1) {
        const receivedKey = req.replace(
          "_required",
          "_received"
        ) as keyof ProjectHandoverData;
        const received = formData[receivedKey];

        // If received is null/undefined, form is not valid
        if (received === null || received === undefined) {
          return false;
        }
      }
    }

    return true;
  };

  const canSave =
    userStore.hasPermission(EmployeeRoles.SEND_CLIENT_HANDOVER_EMAILER, true) &&
    isFormValid();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      slotProps={{
        transition: {
          timeout: 300,
          sx: {
            backgroundColor: theme.palette.customColors.backdrop,
            backdropFilter: "blur(4px)",
          },
        },
        backdrop: {
          timeout: 300,
          sx: {
            backgroundColor: theme.palette.customColors.backdrop,
            backdropFilter: "blur(4px)",
          },
        },
        paper: {
          sx: {
            borderRadius: "16px",
            boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
            overflow: "hidden",
            maxHeight: "90vh",
            maxWidth: {
              xs: "95vw",
              sm: "90vw",
              md: "80vw",
              lg: "70vw",
              xl: "60vw",
            },
            width: {
              xs: "95vw",
              sm: "90vw",
              md: "80vw",
              lg: "70vw",
              xl: "60vw",
            },
            margin: { xs: "16px", sm: "32px" },
            background: `linear-gradient(135deg, ${theme.palette.customColors.white} 0%, ${theme.palette.customColors.lightGreyColor} 100%)`,
          },
        },
      }}
      sx={{
        zIndex: 2000,
        "& .MuiDialog-container": {
          alignItems: "center",
          justifyContent: "center",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "4px",
            background: `linear-gradient(90deg, ${theme.palette.customColors.blueGradientStart} 0%, ${theme.palette.customColors.blueGradientEnd} 50%, ${theme.palette.customColors.blueGradientStart} 100%)`,
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: { xs: "12px 16px 12px", sm: "16px 20px 16px" },
            borderBottom: `1px solid ${theme.palette.customColors.greyBorder}`,
          }}
        >
          <CustomTypography variant="heading3">
            Handover Call Details - Order Requirements
          </CustomTypography>
          <IconButton
            onClick={onClose}
            sx={{
              color: theme.palette.customColors.greyTextColor,
              "&:hover": {
                backgroundColor: theme.palette.customColors.lightGreyColor,
                color: theme.palette.customColors.blackBorder,
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent
          sx={{
            padding: { xs: "20px", sm: "24px" },
            flex: 1,
            overflow: "auto",
            maxHeight: "60vh",
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: theme.palette.customColors.greyBackground,
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: theme.palette.customColors.greyDarkborder,
              borderRadius: "4px",
              "&:hover": {
                backgroundColor: theme.palette.customColors.greyTextColor,
              },
            },
          }}
        >
          {/* Requirements Section */}
          <Box sx={{ mb: 4 }}>
            <RequirementSection
              title="Logo Files"
              requiredKey="log_files_required"
              receivedKey="log_files_received"
              description="Brand logos and design assets needed for this order"
            />

            <RequirementSection
              title="Size Mix Files"
              requiredKey="size_mix_files_required"
              receivedKey="size_mix_files_received"
              description="Size specifications and mix requirements for products"
            />

            <RequirementSection
              title="Insert Content"
              requiredKey="insert_content_required"
              receivedKey="insert_content_received"
              description="Additional content or inserts to be included"
            />

            <RequirementSection
              title="Address File"
              requiredKey="address_file_required"
              receivedKey="address_file_received"
              description="Shipping addresses and delivery information"
            />
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Expected Dispatch Date */}
          <Box
            sx={{
              mb: 4,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <CustomTypography
              variant="heading5"
              style={{ fontWeight: 600, marginBottom: 2 }}
            >
              Expected Dispatch Date
            </CustomTypography>
            {formatDateWithoutTime(formData?.expected_dispatch_date)}
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Products Table */}
          <Box sx={{ mb: 4 }}>
            <CustomTypography
              variant="heading5"
              style={{ fontWeight: 600, marginBottom: 10 }}
            >
              Products & Variants
            </CustomTypography>

            <TableContainer
              component={Paper}
              sx={{
                borderRadius: "12px",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                overflow: "hidden",
              }}
            >
              <Table>
                <TableHead>
                  <TableRow
                    sx={{
                      backgroundColor:
                        theme.palette.customColors.lightGreyColor,
                    }}
                  >
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.customColors.blackBorder,
                      }}
                    >
                      Product Name
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.customColors.blackBorder,
                      }}
                    >
                      Variant
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.customColors.blackBorder,
                      }}
                    >
                      Printing Type
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {formData?.products?.map((product, index) => (
                    <TableRow key={`${product.product_id}-${index}`} hover>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            color: product.product_name
                              ? theme.palette.customColors.blackBorder
                              : theme.palette.customColors.greyTextColor,
                            fontStyle: product.product_name
                              ? "normal"
                              : "italic",
                          }}
                        >
                          {product.product_name || "No product name"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            color: product.variants
                              ? theme.palette.customColors.blackBorder
                              : theme.palette.customColors.greyTextColor,
                            fontStyle: product.variants ? "normal" : "italic",
                          }}
                        >
                          {product.variants || "No variant"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            color: product.printing_type
                              ? theme.palette.customColors.blackBorder
                              : theme.palette.customColors.greyTextColor,
                            fontStyle: product.printing_type
                              ? "normal"
                              : "italic",
                          }}
                        >
                          {product.printing_type || "No customization"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </DialogContent>

        {/* Footer Actions */}
        <DialogActions
          sx={{
            padding: { xs: "16px 20px 20px", sm: "20px 24px 24px" },
            borderTop: `1px solid ${theme.palette.customColors.greyBorder}`,
            backgroundColor: theme.palette.customColors.lightGreyColor,
            gap: 2,
            justifyContent: "space-between",
          }}
        >
          <Box>
            {formData?.emails_count !== undefined &&
              formData?.emails_count !== null &&
              formData?.emails_count > 0 && (
                <CustomTypography variant="body2">
                  {formData?.emails_count} emails sent.{" "}
                  {formData?.email_sent ? (
                    <>
                      Last email sent by{" "}
                      <span style={{ fontWeight: 600 }}>
                        {formData?.email_sent_by_name}
                      </span>
                      {" on "}
                      <span style={{ fontWeight: 600 }}>
                        {formatDate(formData?.email_sent_date)}
                      </span>
                    </>
                  ) : (
                    ""
                  )}
                </CustomTypography>
              )}
          </Box>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              onClick={onClose}
              variant="outlined"
              sx={{
                borderColor: theme.palette.customColors.greyDarkborder,
                color: theme.palette.customColors.greyTextColor,
                fontWeight: 600,
                padding: "10px 24px",
                borderRadius: "8px",
                "&:hover": {
                  borderColor: theme.palette.customColors.greyTextColor,
                  backgroundColor: theme.palette.customColors.lightGreyColor,
                },
              }}
            >
              Cancel
            </Button>

            <Button
              onClick={() => formData && onSave(formData)}
              variant="contained"
              disabled={!canSave}
              sx={{
                backgroundColor: canSave
                  ? theme.palette.customColors.EmeraldGreen
                  : theme.palette.customColors.greyDarkborder,
                color: theme.palette.customColors.white,
                fontWeight: 600,
                padding: "10px 24px",
                borderRadius: "8px",
                "&:hover": {
                  backgroundColor: canSave
                    ? theme.palette.customColors.greenDark
                    : theme.palette.customColors.greyDarkborder,
                  transform: canSave ? "translateY(-1px)" : "none",
                  boxShadow: canSave
                    ? `0 4px 12px ${theme.palette.customColors.EmeraldGreen}30`
                    : "none",
                },
                "&:disabled": {
                  backgroundColor: theme.palette.customColors.greyDarkborder,
                  color: theme.palette.customColors.white,
                },
              }}
            >
              Save
            </Button>
            <Button
              onClick={() => formData && sendEmail(formData, true)}
              variant="contained"
              disabled={
                !canSave || formData?.id === undefined || formData?.id === null
              }
              sx={{
                backgroundColor: canSave
                  ? theme.palette.customColors.blueGradientStart
                  : theme.palette.customColors.greyDarkborder,
                color: theme.palette.customColors.white,
                fontWeight: 600,
                padding: "10px 24px",
                borderRadius: "8px",
                "&:hover": {
                  backgroundColor: canSave
                    ? theme.palette.customColors.blueGradientHoverStart
                    : theme.palette.customColors.greyDarkborder,
                  transform: canSave ? "translateY(-1px)" : "none",
                  boxShadow: canSave
                    ? `0 4px 12px ${theme.palette.customColors.blueGradientStart}30`
                    : "none",
                },
                "&:disabled": {
                  backgroundColor: theme.palette.customColors.greyDarkborder,
                  color: theme.palette.customColors.white,
                },
              }}
            >
              Send Preview Email
            </Button>
            <Button
              onClick={() => formData && sendEmail(formData, false)}
              variant="contained"
              disabled={
                !canSave || formData?.id === undefined || formData?.id === null
              }
              sx={{
                backgroundColor: canSave
                  ? theme.palette.customColors.blueGradientStart
                  : theme.palette.customColors.greyDarkborder,
                color: theme.palette.customColors.white,
                fontWeight: 600,
                padding: "10px 24px",
                borderRadius: "8px",
                "&:hover": {
                  backgroundColor: canSave
                    ? theme.palette.customColors.blueGradientHoverStart
                    : theme.palette.customColors.greyDarkborder,
                  transform: canSave ? "translateY(-1px)" : "none",
                  boxShadow: canSave
                    ? `0 4px 12px ${theme.palette.customColors.blueGradientStart}30`
                    : "none",
                },
                "&:disabled": {
                  backgroundColor: theme.palette.customColors.greyDarkborder,
                  color: theme.palette.customColors.white,
                },
              }}
            >
              Send Email
            </Button>
          </Box>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default HandoverCallDetailsPopup;
